/**
   Copyright (c)2021 3Key Company s.r.o., All Rights Reserved

   It is strictly prohibited to distribute the source code,
   transpiled and compiled code or its parts without previous
   agreement of the 3Key Company board.
*/

import "./SelectedAssetFilters.css";

import React from "react";

import Context from "lib/context";
import ServicesContext from "services/ServicesContext";

import { catchError, debounceTime, Subject, Subscription, switchMap, tap } from "rxjs";

import { bindActionCreators, Dispatch } from "@reduxjs/toolkit";
import { connect } from "react-redux";

import { AssetGroupsState, slice as assetGroupsSlice } from "store/company/assetGroups.slice";
import { SelectedAssetFiltersState as RSelectedAssetFiltersState, slice } from "store/company/selectedAssetFilters.slice";
import { SelectedAssetFiltersProps, SelectedAssetFiltersState } from "./SelectedAssetFilters.types";

import Widget from "components/ThreeKey/Widget";
import ProductFilterList from "./ProductFilterList/ProductFilterList";
import InputDropDown from "components/ThreeKey/InputDropDown";
import MessageBox from "components/ThreeKey/Dialogs/MessageBox";
import SaveAsDialog from "./SaveAsDialog";

class SelectedAssetFilters extends React.Component<SelectedAssetFiltersProps, SelectedAssetFiltersState> {

   static contextType = Context;
   public context!: ServicesContext;

   private checkAssetGroupWritePermissions$ = new Subject<string>();
   private checkAssetGroupWritePermissionsSub?: Subscription;

   public constructor(props: SelectedAssetFiltersProps) {

      super(props)

      this.state = {
         modifiedDialogShown: false,
         saveAsDialogShown: false,
         manageAssetGroupsDialogShown: false,
         deleteDialogShown: false,
         afterDiscardAssetGroupName: "",
         checkingAssetGroupWritePermissions: [],
         assetGroupHasWritePermissions: {}
      }

   }

   public componentDidMount() {

      this.checkAssetGroupWritePermissionsSub = this.checkAssetGroupWritePermissions$.pipe(
         tap(
            assetGroupName => this.setState({
               checkingAssetGroupWritePermissions: [ ...this.state.checkingAssetGroupWritePermissions, assetGroupName ],
               assetGroupHasWritePermissions: { ...this.state.assetGroupHasWritePermissions, [ assetGroupName ]: false }
         })),
         debounceTime(200),
         switchMap(
            assetGroupName => assetGroupName !== "" ?
               this.context.api.companyDataProvider.checkAssetGroupWritePermissions(this.props.company.id, assetGroupName).pipe(
                  tap(assetGroupWritePermissions => {
                     const arr = [ ...this.state.checkingAssetGroupWritePermissions ];
                     arr.splice(this.state.checkingAssetGroupWritePermissions.indexOf(assetGroupName) , 1)
                     this.setState({
                        assetGroupHasWritePermissions: { ...this.state.assetGroupHasWritePermissions, [ assetGroupName ]: assetGroupWritePermissions },
                        checkingAssetGroupWritePermissions: arr
                     });
                  }),
                  catchError(
                     e => {
                        const arr = [ ...this.state.checkingAssetGroupWritePermissions ];
                        arr.splice(this.state.checkingAssetGroupWritePermissions.indexOf(assetGroupName) , 1)
                        this.setState({ checkingAssetGroupWritePermissions: arr });
                        console.error(e);
                        return assetGroupName;
                     }
                  )
               )
            : (() => {
               const arr = [ ...this.state.checkingAssetGroupWritePermissions ];
               arr.splice(this.state.checkingAssetGroupWritePermissions.indexOf(assetGroupName) , 1)
               this.setState({
                  assetGroupHasWritePermissions: { ...this.state.assetGroupHasWritePermissions, [ assetGroupName ]: true },
                  checkingAssetGroupWritePermissions: arr
               });

               return "";
            })()
         )
      ).subscribe();

   }

   public componentWillUnmount() {

      this.checkAssetGroupWritePermissionsSub?.unsubscribe();

   }

   componentDidUpdate(prevProps: SelectedAssetFiltersProps) {

      if (this.props.assetGroups.selectedAssetGroup && this.props.assetGroups.selectedAssetGroup !== prevProps.assetGroups.selectedAssetGroup) {
         this.checkAssetGroupWritePermissions$.next(this.props.assetGroups.selectedAssetGroup.name);
      }

   }


   public render(): React.ReactNode {

      let ret: JSX.Element | JSX.Element[];

      const hasWritePerms = this.props.assetGroups.selectedAssetGroup ?
         this.state.checkingAssetGroupWritePermissions.indexOf(this.props.assetGroups.selectedAssetGroup.name) === -1 ?
            this.state.assetGroupHasWritePermissions[this.props.assetGroups.selectedAssetGroup.name]
         :
            false
      :
         true;

      const saveButtonsDisabled = !this.props.assetGroups.selectedAssetGroup || this.props.assetGroups.selectedAssetGroup.name === "" || !this.props.selectedAssetFilters.modified || !hasWritePerms;
      const saveAsButtonsDisabled = this.props.selectedAssetFilters.filters.length === 0;
      const deleteButtonDisabled = this.props.assetGroups.selectedAssetGroup === undefined || !hasWritePerms;

      const modifiedDialogBox = !this.state.modifiedDialogShown ? <></> :
         <MessageBox
            caption="Asset group filters has been modified"
            message="Do you want to discard unsaved changes?"
            hasCancel={true}
            cancelOnEscape={true}
            cancelOnOverlayMouseDown={true}
            okText="Discard"
            onOk={this.onModifiedDialogBoxOk}
            onCancel={() => this.setState({ modifiedDialogShown: false })}
         />;

      const deleteDialog = !this.state.deleteDialogShown ? <></> :
         <MessageBox
            caption="Deletion confirmation"
            message="Are you sure you want to delete selected asset group?"
            hasCancel={true}
            cancelOnEscape={true}
            cancelOnOverlayMouseDown={true}
            okText="Delete"
            onOk={this.deleteSelectedAssetGroup}
            onCancel={() => this.setState({ deleteDialogShown: false })}
         />;

      const saveAsDialog = !this.state.saveAsDialogShown ? <></> :
         <SaveAsDialog
            validating={this.state.checkingAssetGroupWritePermissions}
            hasWritePermissions={this.state.assetGroupHasWritePermissions}
            onCheckExistsWithoutPermissions={(val) => this.checkAssetGroupWritePermissions$.next(val)}

            assetGroups={this.props.assetGroups.assetGroups.map(ag => `${ag.name}|${ag.description}`)}
            onSave={this.onSaveAs}
            onClose={() => this.setState({ saveAsDialogShown: false })}
         />;

      const safControls = (

         <>
            <span>Selected Assets</span>

            <span className="controls">

               <span>Asset group&nbsp;&nbsp;&nbsp;</span>

               <InputDropDown
                  id="assetGroupName"
                  componentName="asset-group-name"
                  readOnly={true}
                  canClear={true}
                  value={this.props.assetGroups.selectedAssetGroup ? this.props.assetGroups.selectedAssetGroup.name : ""}
                  items={this.props.assetGroups.assetGroups?.map(a => a.name).sort()}
                  dropDownOnFocus={true}
                  onChange={this.onAssetGroupChange}
                  isLoading={this.props.assetGroups.assetGroupsLoading}
               />

               &nbsp;
               <button
                  className="save"
                  data-type="img"
                  disabled={saveButtonsDisabled}
                  title={ saveButtonsDisabled ? "" : "Save current filters to selected asset group" }
                  onClick={ this.onSaveButtonClick }
               />
               &nbsp;
               <button
                  className="save-as"
                  data-type="img"
                  disabled={saveAsButtonsDisabled}
                  onClick={ () => this.setState({ saveAsDialogShown: true }) }
                  title={ saveAsButtonsDisabled ? "" : "Save current filter set as another asset group" }
               />
               &nbsp;
               <button
                  className="trash"
                  data-type="img"
                  disabled={deleteButtonDisabled}
                  onClick={ () => this.setState({ deleteDialogShown: true }) }
                  title={ deleteButtonDisabled ? "" : "Deletes the current asset group" }
               />
               &nbsp;
               <button
                  className="settings"
                  data-type="img"
                  disabled={true}
                  title="Manage asset groups"
                  onClick={ () => this.setState({ manageAssetGroupsDialogShown: true }) }
               />

            </span>

            <span className="dialogs">
               {modifiedDialogBox}
               {saveAsDialog}
               {deleteDialog}
            </span>

         </>

      );


      let productFiltersList: JSX.Element[] =

         [ ...this.props.selectedAssetFilters.filters ].sort(
            (a, b) => {
               let aa = (a.vendor + a.product).toUpperCase();
               let bb = (b.vendor + b.product).toUpperCase();
               return aa < bb ? -1 : aa > bb ? 1 : 0;
            }
         ).map(

            filter => {

               const key = filter.vendor + filter.product;

               return (

                  <ProductFilterList
                     key={key}
                     filter={filter}
                  />
               )

            }
         )

      if (this.props.selectedAssetFilters.filtersLoading) {
         ret = <div className="spinner"></div>
      } else {
         ret = productFiltersList;
      }

      return (
         <Widget componentName="product-filter-list" caption={safControls} isLoading={this.props.isLoading}>

            <div className="selected-asset-filters-component-default">
               {ret}
            </div>

         </Widget>
      )

   }

   private onAssetGroupChange = (name: string) => {

      if (!this.props.selectedAssetFilters.modified) {
         this.selectAssetGroup(name);
         return;
      }

      this.setState({
         modifiedDialogShown: true,
         afterDiscardAssetGroupName: name
      });

   }

   private onModifiedDialogBoxOk = () => {

      this.setState({
         modifiedDialogShown: false,
         afterDiscardAssetGroupName: ""
      })

      this.selectAssetGroup(this.state.afterDiscardAssetGroupName)
   }

   private selectAssetGroup = (name: string) => {

      if (name) {
         this.props.actions.selectAssetGroupByName({ name })
      } else {
         this.props.actions.selectAssetGroupById({ id: "" });
      }

   }

   private onSaveButtonClick = () => {

      if (!this.props.assetGroups.selectedAssetGroup) return;
      this.props.actions.saveSelectedAssetFilters({ assetGroup: this.props.assetGroups.selectedAssetGroup });

   }

   private onSaveAs = (assetGroupName: string, assetGroupDescription: string, merge: boolean) => {

      const assetGroup = this.props.assetGroups.assetGroups.find(ag => ag.name === assetGroupName);
      if (assetGroup) {
         this.props.actions.saveSelectedAssetFiltersAs({ companyId: this.props.company.id, assetGroupOrName: assetGroup, assetGroupDescription, merge });
      } else {
         this.props.actions.saveSelectedAssetFiltersAs({ companyId: this.props.company.id, assetGroupOrName: assetGroupName, assetGroupDescription, merge: false });
      }

      this.setState({ saveAsDialogShown: false })

   }

   private deleteSelectedAssetGroup = () => {

      this.setState({ deleteDialogShown: false })

      if (!this.props.assetGroups.selectedAssetGroup) return
      this.props.actions.deleteAssetGroupById({ id: this.props.assetGroups.selectedAssetGroup.id });

   }

}


const mapStateToProps = (state: {
   assetGroups: AssetGroupsState,
   selectedAssetFilters: RSelectedAssetFiltersState,
}) => {
   return state;
}


const mapDispatchToProps = (dispatch: Dispatch) => {

   return {
      actions: bindActionCreators(
         Object.assign(
            {},
            assetGroupsSlice.actions,
            slice.actions
         ),
         dispatch
      )
   }

}

export default connect(
   mapStateToProps,
   mapDispatchToProps
)(SelectedAssetFilters);
